import {isUndefined} from 'lodash';
import React, {FC, ReactNode, Ref, useCallback, useRef} from 'react';
import styled, {css} from 'styled-components';

import {
  ClickEventHandler,
  FrontClickEvent,
  Handler,
  HandlerOf,
  MouseEventHandler,
} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {ChromelessButton} from '../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon, IconName} from '../../../../libs/shared/ui/src/components/icon/icon';
import {InteractionParent} from '../../../../libs/shared/ui/src/interactionTracking/interactionParent';
import {CellsConversationsDragItem} from '../../../helpers/conversationCellHelpers';
import {
  ConversationActionContextFinder,
  ConversationStatusesEnum,
  isConversationInTeamContextOrSelfAssigned,
  isInternalConversation,
  makeFindConversationActionContext,
  UnreadStatusesEnum,
} from '../../../helpers/models/conversationHelpers';
import {useAppDispatch} from '../../../helpers/reduxHelpers';
import {ActivityModel} from '../../../models/api/activityModel';
import {ConversationHighlightModel} from '../../../models/api/conversationHighlightModel';
import {ConversationBaseModel} from '../../../models/api/conversationModel';
import {ConversationTrackerStatusesEnum} from '../../../models/api/conversationTrackerModel';
import {DraftSummaryModel} from '../../../models/api/draftSummaryModel';
import {ExternalChannelTypeModel} from '../../../models/api/externalChannelTypeModel';
import {InboxModel} from '../../../models/api/inboxModel';
import {MessageModel} from '../../../models/api/messageModel';
import {TagModel} from '../../../models/api/tagModel';
import {TeammateModel} from '../../../models/api/teammateModel';
import {TeamModel} from '../../../models/api/teamModel';
import {VisualRecipientModel} from '../../../models/api/visualRecipientModel';
import {CellsActionsEnum, InterfaceEventsEnum, trackInterfaceEvent} from '../../../services/tracking';
import {
  BumpCommentModel,
  CommentDraftSummary,
  LastCommentDraftModel,
  LastCommentModel,
} from '../../../store/data/conversations/conversationCellSelectors';
import {toggleMarkConversationAsSeenAction} from '../../../store/data/conversations/seenConversationsActions';
import {setConversationsStatusAction} from '../../../store/data/conversations/statusConversationsActions';
import {CellsContext} from '../../../store/panels/cells/context/cellsContext';
import {BlockLink} from '../../common/links/blockLink';
import {CellBase} from './cellBase';
import {CellActivity, VisualSearchResultCommentSummary} from './content/cell/cellActivity';
import {CellAttachmentIcon} from './content/cell/cellAttachmentIcon';
import {CellDate} from './content/cell/cellDate';
import {StyledCellHoverIconsWrapper} from './content/cell/cellHoverIcons';
import {CellHoverIconsContainer} from './content/cell/cellHoverIconsContainer';
import {CellInboxColor} from './content/cell/cellInboxColor';
import {CellInternalIcon} from './content/cell/cellInternalIcon';
import {CellMiddle} from './content/cell/cellMiddle';
import {CellOverlay, CellOverlayProps} from './content/cell/cellOverlay';
import {CellRecipient} from './content/cell/cellRecipient';
import {VisualSearchActivityModel} from './content/cell/cellSearchActivitySummaryHighlight';
import {CellStatus, getConversationStatusIconName, StyledCellStatusIconDiv} from './content/cell/cellStatus';
import {CellTeam} from './content/cell/cellTeam';
import {ConversationCellDragSource} from './conversationCellDragSource';

/*
 * Props.
 */

export type ConversationCellClickEventHandler = (
  conversationId: number,
  event: FrontClickEvent<HTMLElement>,
) => void;
export type ConversationCellDoubleClickHandler = (conversationId: number) => void;
type SetConversationStatusHandler = (
  cellsContext: CellsContext,
  conversationIds: readonly number[],
  newStatus: ConversationStatusesEnum,
  findConversationActionContext: ConversationActionContextFinder,
) => void;

export interface ConversationCellProps {
  sourceCustomViewId: number | undefined;
  cellsContext: CellsContext;
  mentionableAliases?: ReadonlySet<string>;
  conversation: ConversationBaseModel;
  date: number;
  status: ConversationStatusesEnum;
  unreadStatus: UnreadStatusesEnum;
  trackerStatus?: ConversationTrackerStatusesEnum;
  trackerTeammates: ReadonlyArray<TeammateModel>;
  tags: ReadonlyArray<TagModel>;
  recipient?: VisualRecipientModel;
  assignee?: TeammateModel;
  team?: TeamModel;
  inbox?: InboxModel;
  commentingTeammate?: TeammateModel;
  replyingTeammate?: TeammateModel;
  draftSeedAuthors?: ReadonlyArray<TeammateModel>;
  lastComment?: LastCommentModel;
  lastCommentDraft?: LastCommentDraftModel;
  commentDraftSummary?: CommentDraftSummary;
  lastManualMessage: MessageModel | undefined;
  reminderDate?: number;
  draft?: DraftSummaryModel;
  overlay?: CellOverlayProps;
  isSelected?: boolean;
  bumpActivity?: ActivityModel;
  bumpComment?: BumpCommentModel;
  bumpTeammateSource?: TeammateModel;
  path?: string;
  onClick?: ConversationCellClickEventHandler;
  onDoubleClick?: ConversationCellDoubleClickHandler;
  isStarred?: boolean;
  originalSeenAt?: number;
  innerRef?: Ref<HTMLDivElement>;
  isPreview?: boolean;
  userId?: number;
  hasP2QuickActions: boolean;
  numMessages?: number;
  externalChannelType?: ExternalChannelTypeModel;
  mostRecentClickConversationIdRef?: React.MutableRefObject<number>;
  secondMostRecentClickConversationIdRef?: React.MutableRefObject<number>;
  areHoverActionsEnabled?: boolean;
  customDragItem?: CellsConversationsDragItem;
  customDragMessages?: readonly MessageModel[];

  // Search
  conversationHighlight?: ConversationHighlightModel;
  searchActivity?: VisualSearchActivityModel;
  searchResultCommentSummary?: VisualSearchResultCommentSummary;
}

/*
 * Style.
 */

// We wrap the component so that we can target it in a selector below.
const StyledCellAttachmentRightIcon = styled(CellAttachmentIcon)``;

interface StyledCellBaseProps {
  $shouldShowQuickActionsOnCellHover: boolean;
}

const StyledCellBase = styled(CellBase)<StyledCellBaseProps>`
  ${(p) => addCellBaseHoverStyles(p)}
`;

function addCellBaseHoverStyles(p: StyledCellBaseProps) {
  if (p.$shouldShowQuickActionsOnCellHover) {
    return css`
      &:hover ${StyledCellHoverIconsWrapper} {
        display: flex;
      }
      &:hover ${StyledCellStatusIconDiv} {
        display: none;
      }
    `;
  }

  return '';
}

const StyledBottomRightWrapperDiv = styled.div`
  grid-area: bottom-right;
  width: 100%;
  display: flex;
  justify-content: flex-end;
  & > div:nth-child(2) {
    margin-left: 8px;
  }
`;

const StyledTopLineWrapperDiv = styled.div`
  grid-area: top-line;
  display: flex;
  align-items: center;
  justify-content: space-between;
  & > div:nth-child(2) {
    margin-left: 6px;
  }
`;

/*
 * Component.
 */

export const ConversationCell: FC<ConversationCellProps> = (props) => {
  const {cellsContext, conversation, path, originalSeenAt, hasP2QuickActions, reminderDate} = props;
  const {id} = conversation;
  const isVisuallySelected = props.isSelected;
  const prevMouseDownTimeStampRef = useRef(0);
  const isDoubleClickDisabledRef = useRef(false);

  const interactionId = `conversation-cell-${id}`;

  const {mostRecentClickConversationIdRef, secondMostRecentClickConversationIdRef} = props;

  const onClick: ClickEventHandler = (event) => {
    if (!props.onClick) {
      return;
    }

    if (mostRecentClickConversationIdRef && secondMostRecentClickConversationIdRef) {
      secondMostRecentClickConversationIdRef.current = mostRecentClickConversationIdRef.current;
      mostRecentClickConversationIdRef.current = conversation.id;
    }

    props.onClick(id, event);
  };

  const dispatch = useAppDispatch();

  const isRead = props.unreadStatus === UnreadStatusesEnum.NONE;
  const onToggleMarkAsRead = useCallback(() => {
    // We want to send the originalSeenAt only in the case of the read -> unread transition.
    const seenAt = isRead ? originalSeenAt : undefined;

    dispatch(toggleMarkConversationAsSeenAction([id], !isRead, {cellsContext, seenAt}));
  }, [dispatch, id, isRead, cellsContext, originalSeenAt]);

  const onSetConversationsStatus = useCallback(
    (
      currentCellsContext: CellsContext,
      conversationIds: readonly number[],
      newStatus: ConversationStatusesEnum,
      findConversationActionContext: ConversationActionContextFinder,
    ) => {
      dispatch(
        setConversationsStatusAction(
          currentCellsContext,
          conversationIds,
          newStatus,
          findConversationActionContext,
        ),
      );
    },
    [dispatch],
  );

  const onDoubleClick: MouseEventHandler = (event) => {
    if (!props.onDoubleClick || isDoubleClickDisabledRef.current) {
      return;
    }

    if (event.defaultPrevented) {
      return;
    }

    // The two clicks are not for the same conversation ID.
    if (
      mostRecentClickConversationIdRef &&
      secondMostRecentClickConversationIdRef &&
      mostRecentClickConversationIdRef.current !== secondMostRecentClickConversationIdRef.current
    ) {
      return;
    }

    props.onDoubleClick(id);
  };

  const onMouseDown: MouseEventHandler = (event) => {
    isDoubleClickDisabledRef.current = event.timeStamp === prevMouseDownTimeStampRef.current;
    prevMouseDownTimeStampRef.current = event.timeStamp;
  };

  const {inSpam, inTrash} = cellsContext;

  const shouldShowQuickActionsOnCellHover =
    hasP2QuickActions && !inSpam && !inTrash && Boolean(props.areHoverActionsEnabled) && !reminderDate; // TODO: Remove reminderDate check when snooze implemented
  const hasDoubleBottomRightIcons =
    props.team && props.conversation.hasAttachments && props.hasP2QuickActions;

  const cell = (
    <StyledCellBase
      $shouldShowQuickActionsOnCellHover={shouldShowQuickActionsOnCellHover}
      isSelected={isVisuallySelected}
      isPreview={props.isPreview}
      hasDoubleBottomRight={hasDoubleBottomRightIcons}
      hasDoubleTopLine={props.hasP2QuickActions}
    >
      {renderTopLine(props, onToggleMarkAsRead, shouldShowQuickActionsOnCellHover, onSetConversationsStatus)}
      {renderMiddleLine(props, onToggleMarkAsRead)}
      {renderBottomLine(props)}
      {renderAbsolute(props)}
    </StyledCellBase>
  );

  if (!props.onClick) {
    return cell;
  }

  // TODO: Use role=option with aria-selected instead of a button?
  const clickableCell = isUndefined(path) ? (
    <ChromelessButton
      ref={props.innerRef}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      onMouseDown={onMouseDown}
      interactionId={interactionId}
    >
      {cell}
    </ChromelessButton>
  ) : (
    <BlockLink
      ref={props.innerRef as any}
      to={path}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      onMouseDown={onMouseDown}
      interactionId={interactionId}
    >
      {cell}
    </BlockLink>
  );

  return (
    <InteractionParent interactionId={`conversation-cell-${id}`}>
      <ConversationCellDragSource
        cellsContext={props.cellsContext}
        sourceCustomViewId={props.sourceCustomViewId}
        conversation={props.conversation}
        lastManualMessage={props.lastManualMessage}
        customDragItem={props.customDragItem}
        customDragMessages={props.customDragMessages}
      >
        {clickableCell}
      </ConversationCellDragSource>
    </InteractionParent>
  );
};

/*
 * Helpers.
 */

function renderTopLine(
  props: ConversationCellProps,
  toggleMarkAsRead: Handler,
  /** Whether this particular cell will display quick actions when the cell is hovered. */
  shouldShowQuickActionsOnCellHover: boolean,
  onSetConversationStatus: SetConversationStatusHandler,
) {
  const {
    conversation,
    conversationHighlight,
    isSelected,
    status,
    unreadStatus,
    reminderDate,
    cellsContext,
    hasP2QuickActions,
  } = props;

  const onArchiveClick: HandlerOf<FrontClickEvent> = (event) => {
    event.preventDefault();

    onSetConversationStatus(
      cellsContext,
      [conversation.id],
      ConversationStatusesEnum.ARCHIVED,
      makeFindConversationActionContext(cellsContext, [conversation]),
    );
    trackInterfaceEvent(InterfaceEventsEnum.P2_CELL_CLICK, {
      action: CellsActionsEnum.ARCHIVE,
    });
  };

  const onOpenClick: HandlerOf<FrontClickEvent> = (event) => {
    event.preventDefault();

    trackInterfaceEvent(InterfaceEventsEnum.P2_CELL_CLICK, {
      action:
        props.status === ConversationStatusesEnum.ARCHIVED
          ? CellsActionsEnum.REOPEN
          : CellsActionsEnum.RESTORE,
    });
    onSetConversationStatus(
      cellsContext,
      [conversation.id],
      ConversationStatusesEnum.OPEN,
      makeFindConversationActionContext(cellsContext, [conversation]),
    );
  };

  const onTrashClick: HandlerOf<FrontClickEvent> = (event) => {
    event.preventDefault();

    onSetConversationStatus(
      cellsContext,
      [conversation.id],
      ConversationStatusesEnum.TRASHED,
      makeFindConversationActionContext(cellsContext, [conversation]),
    );
    trackInterfaceEvent(InterfaceEventsEnum.P2_CELL_CLICK, {
      action: CellsActionsEnum.TRASH,
    });
  };

  const iconName = getConversationStatusIconName(status);

  const maybeWrapTopLine = (children: ReactNode) => {
    if (!hasP2QuickActions) {
      return children;
    }

    return <StyledTopLineWrapperDiv>{children}</StyledTopLineWrapperDiv>;
  };

  return (
    <>
      <CellStatus
        status={props.status}
        unreadStatus={props.unreadStatus}
        isSelected={props.isSelected}
        toggleMarkAsRead={toggleMarkAsRead}
        hasP2QuickActions={hasP2QuickActions}
        hasReminder={Boolean(props.reminderDate)}
      />
      {maybeWrapTopLine(
        <>
          <CellRecipient
            conversation={props.conversation}
            recipientHighlight={conversationHighlight && conversationHighlight.recipient}
            trackerTeammates={props.trackerTeammates}
            recipient={props.recipient}
            assignee={props.assignee}
            inbox={props.inbox}
            lastCommentAuthor={props.lastComment?.author}
            isSelected={props.isSelected}
            isUnread={props.unreadStatus !== UnreadStatusesEnum.NONE}
          />
          <CellDate
            date={props.date}
            reminderDate={props.reminderDate}
            isSelected={isSelected}
            hasGradientOverlay={false}
            shouldDisableFormatToggling={false}
            hasP2QuickActions={hasP2QuickActions}
          />
        </>,
      )}
      {renderTopLineRightIcon(
        conversation.hasAttachments,
        isSelected,
        status,
        unreadStatus,
        iconName,
        hasP2QuickActions,
        reminderDate,
      )}
      {shouldShowQuickActionsOnCellHover && (
        <CellHoverIconsContainer
          onArchiveClick={onArchiveClick}
          onTrashClick={onTrashClick}
          onOpenClick={onOpenClick}
          isSelected={isSelected}
          status={status}
          conversation={conversation}
          hasReminder={Boolean(reminderDate)}
          isTeamContext={isConversationInTeamContextOrSelfAssigned(conversation, cellsContext)}
          isDelegated={cellsContext.isDelegation}
        />
      )}
    </>
  );
}

function renderTopLineRightIcon(
  hasAttachments: boolean,
  isSelected: boolean | undefined,
  status: ConversationStatusesEnum,
  unreadStatus: UnreadStatusesEnum,
  iconName: IconName | undefined,
  /** Whether p2-quick-actions FF is on. */
  hasP2QuickActions: boolean,
  reminderDate?: number,
) {
  if (
    hasP2QuickActions &&
    ((status === ConversationStatusesEnum.ARCHIVED && !reminderDate) ||
      status === ConversationStatusesEnum.TRASHED ||
      status === ConversationStatusesEnum.SPAM) &&
    iconName
  ) {
    return (
      <StyledCellStatusIconDiv
        $isSelected={isSelected}
        $status={status}
        $hasP2QuickActions={hasP2QuickActions}
        $unreadStatus={unreadStatus}
      >
        <Icon name={iconName} />
      </StyledCellStatusIconDiv>
    );
  }

  if (hasAttachments && !hasP2QuickActions) {
    return <StyledCellAttachmentRightIcon isSelected={isSelected} hasP2QuickActions={hasP2QuickActions} />;
  }

  return null;
}

function renderMiddleLine(props: ConversationCellProps, toggleMarkAsRead: Handler) {
  const {conversation, conversationHighlight, draft, isSelected, numMessages, externalChannelType} = props;
  const {messageType} = conversation;

  const conversationMessageType = (numMessages === 0 && draft && draft.type) || messageType;
  const draftSubject = draft && draft.subject;
  const subject = draftSubject || conversation.subject || '';

  return (
    <>
      {isInternalConversation(conversation) && <CellInternalIcon isSelected={isSelected} />}

      <CellMiddle
        messageType={conversationMessageType}
        subject={subject}
        subjectHighlight={conversationHighlight && conversationHighlight.subject}
        recipient={props.recipient}
        tags={props.tags}
        badgeValue={numMessages ?? 0}
        isSelected={props.isSelected}
        isStarred={props.isStarred}
        externalChannelType={externalChannelType}
        hasP2QuickActions={props.hasP2QuickActions}
      />
    </>
  );
}

function renderBottomLine(props: ConversationCellProps) {
  return (
    <>
      <CellActivity
        conversationId={props.conversation.id}
        mentionableAliases={props.mentionableAliases}
        summary={props.conversation.summary}
        conversationHighlight={props.conversationHighlight}
        searchActivity={props.searchActivity}
        commentingTeammate={props.commentingTeammate}
        replyingTeammate={props.replyingTeammate}
        draftSeedAuthors={props.draftSeedAuthors}
        lastManualMessage={props.lastManualMessage}
        lastComment={props.lastComment}
        lastCommentDraft={props.lastCommentDraft}
        commentDraftSummary={props.commentDraftSummary}
        isSelected={props.isSelected}
        draft={props.draft}
        bumpActivity={props.bumpActivity}
        bumpComment={props.bumpComment}
        bumpTeammateSource={props.bumpTeammateSource}
        unreadStatus={props.unreadStatus}
        userId={props.userId}
        searchResultCommentSummary={props.searchResultCommentSummary}
      />
      <StyledBottomRightWrapperDiv>
        {props.hasP2QuickActions && props.conversation.hasAttachments && (
          <StyledCellAttachmentRightIcon
            isSelected={props.isSelected}
            hasP2QuickActions={props.hasP2QuickActions}
          />
        )}
        {props.team && <CellTeam team={props.team} />}
      </StyledBottomRightWrapperDiv>
    </>
  );
}

/** Render absolute positioned items. */
function renderAbsolute(props: ConversationCellProps) {
  const {inbox, overlay, isSelected} = props;

  return (
    <>
      {inbox && <CellInboxColor inbox={inbox} />}
      {overlay && (
        <CellOverlay
          isSelected={isSelected}
          type={overlay.type}
          requester={overlay.requester}
          assignee={overlay.assignee}
          inbox={overlay.inbox}
          conversationId={overlay.conversationId}
        />
      )}
    </>
  );
}
